import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, ExternalLink, Plane, Scale, FileText, MessageSquareWarning, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Spinner } from "../common";
import { googleFlightsUrl } from "../../lib/googleFlights";

/* Flight Recovery Assistant results — PUBLIC, reused on /recovery page and in Saved Flights. */
export default function RecoveryOptions({ origin, dest, airline, date }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setData(null); setErr(false);
    api.get("/recovery", { params: { origin, dest, airline: airline || "" } })
      .then((r) => setData(r.data)).catch(() => setErr(true));
  }, [origin, dest, airline]);
  if (err) return <p className="text-sm text-rose-700">Couldn't load recovery options — check the airport codes.</p>;
  if (!data) return <Spinner />;
  const gfUrl = googleFlightsUrl({ type: "oneway", from: data.origin.code, to: data.dest.code, depart: date || "" });
  return (
    <div className="space-y-5" data-testid="recovery-options">
      <div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
          Alternative airlines on {data.origin.code} → {data.dest.code}
        </div>
        {data.alternatives.length ? (
          <div className="flex flex-wrap gap-1.5" data-testid="recovery-alternatives">
            {data.alternatives.map((a) => (
              <span key={a.iata} className="text-xs font-700 border border-sky-300 bg-sky-50 text-sky-800 px-2 py-1">
                {a.name} ({a.iata})
              </span>
            ))}
          </div>
        ) : <p className="text-xs text-muted-foreground">No other airlines found on this exact route — check nearby airports or connections below.</p>}
        <a href={gfUrl} target="_blank" rel="noopener noreferrer" data-testid="recovery-google-flights"
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-700 text-primary hover:underline">
          <Plane className="w-4 h-4" /> See live same-day options on Google Flights <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      <div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Your rights</div>
        <div className="space-y-2" data-testid="recovery-rights">
          {data.rights.map((r, i) => (
            <div key={i} className="border border-zinc-300 bg-white p-3">
              <div className="flex items-center gap-1.5 font-700 text-sm"><Scale className="w-4 h-4 text-emerald-700" /> {r.regime}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{r.applies}</div>
              <p className="text-xs text-zinc-700 mt-1">{r.summary}</p>
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline mt-1">
                Official source <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => {
          const url = `${window.location.origin}/recovery?origin=${data.origin.code}&dest=${data.dest.code}${airline ? `&airline=${airline}` : ""}${date ? `&date=${date}` : ""}`;
          const text = `Flight disruption help — alternatives & passenger rights for ${data.origin.code} → ${data.dest.code}`;
          const copyFallback = () => { navigator.clipboard?.writeText(url); toast.success("Link copied — send it to family so they can follow along"); };
          if (navigator.share) { navigator.share({ title: "Flight Recovery — TravelerPulse", text, url }).catch((e) => { if (e?.name !== "AbortError") copyFallback(); }); }
          else { copyFallback(); }
        }} data-testid="recovery-share"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-sky-700 text-white text-sm font-700 hover:bg-sky-600">
          <Share2 className="w-4 h-4" /> Share with family
        </button>
        <Link to={data.guides.complaint_article} data-testid="recovery-complaint-article"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-500 text-amber-700 text-sm font-700 hover:bg-amber-50">
          <FileText className="w-4 h-4" /> Guide: how to complain to airlines
        </Link>
        <Link to={data.guides.file_complaint} data-testid="recovery-file-complaint"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-rose-400 text-rose-700 text-sm font-700 hover:bg-rose-50">
          <MessageSquareWarning className="w-4 h-4" /> File a complaint on TravelerPulse
        </Link>
      </div>
      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
        <LifeBuoy className="w-3.5 h-3.5 mt-px shrink-0" /> {data.disclaimer}
      </p>
    </div>
  );
}
